import {gql, useQuery} from '@apollo/client';
import {ZeroAddress} from 'ethers';
import {stakingSubgraph} from '@/providers/subgraph/subgraph';

export type Staking = {
  id: string;
  multiplier: string;
  lockDuration: number;
  cumulativeRewards: string;
};

export type Balance = {
  id: string;
  stakeAmount: string;
  stake: {
    id: string;
    stakeId: string;
    initialStakeAmount: string;
    initialTokenAmount: string;
    cumulativeRewards: string;
    staking: Staking;
    cliffDuration: number;
    createdAt: number;
  };
};

export type Owner = {
  id: string;
};

export type NftHistory = {
  id: string;
  nft: {
    id: string;
  };
  balance: Balance;
  owner: Owner;
  image: string;
  startTokenAmount: string;
  endTokenAmount: string;
  startTimestamp: number;
  endTimestamp: number;
};

export type StakeBalanceHistories = {
  id: string;
  stake: {
    id: string;
    staking: Staking;
  };
  stakeAmount: string;
  timestamp: number;
};

export type StakingBalanceHistories = {
  id: string;
  staking: Staking;
  stakeAmount: number;
  timestamp: number;
};

export type StakingData = {
  stakingRewards: {
    staking: Staking;
    tokenAmount: string;
    cumulativeValue: string;
    createdAt: number;
  }[];
  nftHistories: NftHistory[];
  stakeBalanceHistories: StakeBalanceHistories[];
  stakingBalanceHistories: StakingBalanceHistories[];
};

export const useStakingData = (account?: string) => {
  return useQuery<StakingData>(
    gql`
      query StakingRewards($account: String!) {
        stakingRewards(first: 1000, orderBy: createdAt, orderDirection: desc) {
          staking {
            id
            multiplier
            lockDuration
          }
          tokenAmount
          cumulativeValue
          createdAt
        }
        nftHistories(
          first: 1000
          where: {owner: $account}
          orderBy: startTimestamp
          orderDirection: desc
        ) {
          id
          nft {
            id
          }
          balance {
            id
            stakeAmount
            stake {
              id
              stakeId
              cumulativeRewards
              initialStakeAmount
              initialTokenAmount
              cliffDuration
              createdAt
              staking {
                id
                multiplier
                lockDuration
                cumulativeRewards
              }
            }
          }
          owner {
            id
          }
          image
          startTokenAmount
          endTokenAmount
          startTimestamp
          endTimestamp
        }
        stakeBalanceHistories(
          first: 1000
          where: {account: $account}
          orderBy: timestamp
          orderDirection: desc
        ) {
          id
          stake {
            id
            cumulativeRewards
            initialStakeAmount
            initialTokenAmount
            cliffDuration
            createdAt
            staking {
              id
              multiplier
              lockDuration
              cumulativeRewards
            }
          }
          stakeAmount
          timestamp
        }
        stakingBalanceHistories(
          first: 1000
          where: {account: $account}
          orderBy: timestamp
          orderDirection: desc
        ) {
          id
          staking {
            id
            multiplier
            lockDuration
            cumulativeRewards
          }
          stakeAmount
          timestamp
        }
      }
    `,
    {
      client: stakingSubgraph,
      fetchPolicy: 'cache-first',
      variables: {
        account: account?.toLowerCase() || ZeroAddress,
      },
      pollInterval: 3600000, // 1 hour in milliseconds
    }
  );
};
